import Link from 'next/link'
import styled, { keyframes, css } from 'styled-components'
import { MyImage } from '~/components/ui/MyImage'
import {
  CompetitionType,
  getCompetitionList_marketingModule_competitionPage_edges_node,
  StartStatusEnum,
} from '~/graphqls/types'
import { toYMD, digitalUnit } from '~/utils'
import { useIntersectionObserver } from '~/hooks/useIntersectionObserver'
import { IAICompetitionContentObj } from '~/@types/ai'
import { useMemo } from 'react'
/** 单个大赛详情 */
export interface IAICompetitionDetailNode
  extends getCompetitionList_marketingModule_competitionPage_edges_node,
    IAICompetitionContentObj {}

const scrollAni = keyframes`
  0% {
    opacity: 0;
    transform: translate(0, 20%);
  }
  100% {
    opacity: 1;
    transform: translate(0, 0);
  }
`

const animateScroll = css`
  animation: ${scrollAni} 800ms cubic-bezier(0.4, 0, 0.25, 1);
`
const StyledSingleCompetition = styled.div<{ visible?: boolean }>`
  border-radius: 16px;
  margin-bottom: 32px;
  background: rgba(255, 255, 255, 0.04);
  ${({ visible }) => (visible ? animateScroll : null)}
  .competition-thumb-wrap {
    position: relative;
    height: 236px;
    border-radius: 16px 16px 0 0;
    overflow: hidden;
    display: block;
    margin-bottom: 12px;
    &:hover {
      .competition-thumb {
        transform: scale(1.1);
      }
    }
  }
  .competition-thumb {
    height: 100%;
    width: 100%;
    transition: transform ease-in-out 180ms;
  }
  .competition-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .competition-title {
    font-weight: 600;
    font-size: 16px;
    line-height: 24px;
    color: ${props => props.theme['neutral-800']};
    margin-bottom: 4px;
  }
  .competition-date {
    font-size: 12px;
    line-height: 20px;
    color: ${props => props.theme['neutral-400']};
  }
  .competition-status {
    font-size: 12px;
    line-height: 20px;
    font-weight: 400;
    padding: 0px 4px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    img {
      vertical-align: middle;
      margin-right: 2px;
    }
    &--wait {
      background: linear-gradient(90deg, #2507cc 0%, #8e26d4 100%);
      color: #fff;
    }
    &--doing {
      background: ${props => props.theme.error};
      color: #fff;
    }
    &--end {
      background: ${props => props.theme['neutral-300']};
      color: ${props => props.theme['neutral-500']};
    }
  }
`

export const SingleCompetition: React.FC<{
  data: IAICompetitionDetailNode
}> = ({ data }) => {
  const { ref, isVisible } = useIntersectionObserver<HTMLDivElement>({
    // 向下交叉区域延伸一段距离
    rootMargin: '0px 0px 30px 0px',
  })

  const detailUrl = useMemo(() => {
    if (data.type === CompetitionType.OUTSIDE) {
      return data.jump_url_pc
    } else if (
      data.startStatus === StartStatusEnum.WAIT ||
      !data?.participate
    ) {
      return `/ai-competition/${data.id}/intro`
    } else if (data.startStatus === StartStatusEnum.COMPETITION_RESULT) {
      return `/ai-competition/${data.id}/announcement`
    } else {
      return `/ai-competition/${data.id}`
    }
  }, [data])

  return (
    <StyledSingleCompetition ref={ref} visible={isVisible}>
      <Link
        href={detailUrl}
        target={data.type === CompetitionType.OUTSIDE ? '_blank' : '_self'}
      >
        <div className="competition-thumb-wrap">
          <MyImage
            tag="v800"
            className="competition-thumb"
            src={data?.list_cover_pc || data.banner_pc}
            dark
          />
        </div>
      </Link>
      <div className="px-3 pb-3">
        <div className="w-[415px]">
          <Link href={detailUrl}>
            <div className="competition-title line-clamp-1">{data.name}</div>
          </Link>
        </div>
        <div className="competition-info">
          {data.startStatus === StartStatusEnum.WAIT && (
            <span className="competition-status competition-status--wait">
              <img src="/images/icons/hourglass-light.svg" alt="即将开始" />
              即将开始
            </span>
          )}
          {data.startStatus === StartStatusEnum.ING && (
            <span className="competition-status competition-status--doing">
              <img src="/images/icons/clock-icon.svg" alt="进行中" />
              进行中
            </span>
          )}

          {(data.startStatus === StartStatusEnum.END ||
            data.startStatus === StartStatusEnum.COMPETITION_RESULT) && (
            <span className="competition-status competition-status--end">
              <img src="/images/icons/hourglass-dark.svg" alt="已结束" />
              已结束
            </span>
          )}
          {data.startStatus !== StartStatusEnum.WAIT &&
          data.type !== CompetitionType.OUTSIDE &&
          data?.statistics?.viewCount ? (
            <div className="flex">
              <img src="/images/icons/fire--red.svg" alt="" className="mr-1" />
              <span className="competition-date">
                热度：
                {digitalUnit(data?.statistics?.viewCount)}
              </span>
            </div>
          ) : (
            <div className="competition-date">
              活动时间：{toYMD(data.startDateTime)} - {toYMD(data.endDateTime)}
            </div>
          )}
        </div>
      </div>
    </StyledSingleCompetition>
  )
}
